import React, { useState, useEffect } from 'react';
import { 
    FiBook, FiPlus, FiSearch, FiFilter, FiEdit2, FiTrash2, 
    FiFileText, FiCheckSquare, FiMessageSquare, FiStar as FiLightbulb, 
    FiStar, FiTag, FiCalendar, FiUser, FiX
} from 'react-icons/fi';
import { adminAPI } from '../../../services/api';
import '../AdminDashboard.css';
import './TeacherMaterialsPage.css';

const TeacherMaterialsPage = () => {
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingMaterial, setEditingMaterial] = useState(null);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [filters, setFilters] = useState({
        category: '',
        search: ''
    });

    useEffect(() => {
        loadMaterials();
    }, [filters]);

    const loadMaterials = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getTeacherMaterials(filters);
            setMaterials(response.data.materials || []);
        } catch (error) {
            console.error('Ошибка загрузки материалов:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setSelectedMaterial(null);
        setEditingMaterial({
            title: '',
            category: 'other',
            content: '',
            tags: '',
            related_type: 'general',
            related_id: null
        });
        setShowCreateModal(true);
    };

    const handleEdit = (material) => {
        setSelectedMaterial(null);
        setEditingMaterial(material);
        setShowCreateModal(true);
    };

    const handleSave = async () => {
        try {
            if (editingMaterial.id) {
                await adminAPI.updateTeacherMaterial(editingMaterial.id, editingMaterial);
            } else {
                await adminAPI.createTeacherMaterial(editingMaterial);
            }
            setShowCreateModal(false);
            setEditingMaterial(null);
            loadMaterials();
        } catch (error) {
            console.error('Ошибка сохранения:', error);
            alert('Ошибка при сохранении материала');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Удалить этот материал?')) return;
        
        try {
            await adminAPI.deleteTeacherMaterial(id);
            loadMaterials();
        } catch (error) {
            console.error('Ошибка удаления:', error);
            alert('Ошибка при удалении материала');
        }
    };

    const handleTogglePin = async (id) => {
        try {
            await adminAPI.togglePinMaterial(id);
            loadMaterials();
        } catch (error) {
            console.error('Ошибка закрепления:', error);
        }
    };

    const getCategoryIcon = (category) => {
        const icons = {
            plan: FiFileText,
            answers: FiCheckSquare,
            notes: FiMessageSquare,
            tips: FiLightbulb,
            other: FiBook
        };
        const Icon = icons[category] || FiBook;
        return <Icon size={20} />;
    };

    const getCategoryName = (category) => {
        const names = {
            plan: 'План урока',
            answers: 'Ответы',
            notes: 'Заметки',
            tips: 'Подсказки',
            other: 'Другое'
        };
        return names[category] || 'Другое';
    };

    const getCategoryColor = (category) => {
        const colors = {
            plan: '#4a90e2',
            answers: '#52c41a',
            notes: '#faad14',
            tips: '#722ed1',
            other: '#8c8c8c'
        };
        return colors[category] || '#8c8c8c';
    };

    return (
        <div className="admin-content">
            <div className="teacher-materials-page">
                <div className="teacher-materials-header">
                    <div>
                        <h1>Методические материалы</h1>
                        <p>Планы уроков, ответы, подсказки и заметки для учителя</p>
                    </div>
                    <button onClick={handleCreate} className="btn-create-material">
                        <FiPlus /> Создать материал
                    </button>
                </div>

                {/* Фильтры */}
                <div className="teacher-materials-filters">
                    <div className="teacher-materials-filters-inner">
                        <div className="teacher-materials-search">
                            <FiSearch className="search-icon" size={18} />
                            <input
                                type="text"
                                placeholder="Поиск по названию, содержанию или тегам..."
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            />
                        </div>
                        <select
                            className="teacher-materials-category-select"
                            value={filters.category}
                            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                        >
                            <option value="">Все категории</option>
                            <option value="plan">Планы уроков</option>
                            <option value="answers">Ответы</option>
                            <option value="notes">Заметки</option>
                            <option value="tips">Подсказки</option>
                            <option value="other">Другое</option>
                        </select>
                    </div>
                </div>

                {/* Список материалов */}
                {loading ? (
                    <div className="teacher-materials-loading">Загрузка...</div>
                ) : materials.length === 0 ? (
                    <div className="teacher-materials-empty">
                        <FiBook size={48} style={{ color: '#ccc', marginBottom: '15px' }} />
                        <p>
                            {filters.search || filters.category ? 'Материалы не найдены' : 'Нет методических материалов'}
                        </p>
                    </div>
                ) : (
                    <div className="teacher-materials-list">
                        {materials.map(material => (
                            <div
                                key={material.id}
                                className="teacher-material-card"
                                onClick={() => setSelectedMaterial(material)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        setSelectedMaterial(material);
                                    }
                                }}
                            >
                                <div className="teacher-material-header">
                                    {Boolean(material.is_pinned) && (
                                        <FiStar className="pin-icon" size={20} />
                                    )}
                                    <div
                                        className="teacher-material-category"
                                        style={{ background: getCategoryColor(material.category) }}
                                    >
                                        {getCategoryIcon(material.category)}
                                        {getCategoryName(material.category)}
                                    </div>
                                    <h3 className="teacher-material-title">{material.title}</h3>
                                </div>

                                {material.content && (
                                    <p className="teacher-material-content-preview">
                                        {material.content.length > 200
                                            ? material.content.substring(0, 200) + '...'
                                            : material.content}
                                    </p>
                                )}

                                {material.tags && (
                                    <div className="teacher-material-tags">
                                        {material.tags.split(',').map((tag, idx) => (
                                            <span key={idx} className="teacher-material-tag">
                                                <FiTag size={12} /> {tag.trim()}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <div className="teacher-material-meta">
                                    <span>
                                        <FiCalendar size={12} />
                                        {new Date(material.updated_at).toLocaleDateString('ru-RU')}
                                    </span>
                                    {material.author_name && (
                                        <span>
                                            <FiUser size={12} />
                                            {material.author_name}
                                        </span>
                                    )}
                                </div>

                                <div className="teacher-material-actions">
                                    <button
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleTogglePin(material.id);
                                        }}
                                        className={`btn-material-pin ${material.is_pinned ? 'pinned' : ''}`}
                                        title={material.is_pinned ? 'Открепить' : 'Закрепить'}
                                    >
                                        <FiStar size={14} />
                                    </button>
                                    <button
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleEdit(material);
                                        }}
                                        className="btn-material-edit"
                                    >
                                        <FiEdit2 size={14} /> Изменить
                                    </button>
                                    <button
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleDelete(material.id);
                                        }}
                                        className="btn-material-delete"
                                    >
                                        <FiTrash2 size={14} /> Удалить
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

            {/* Просмотр материала */}
            {selectedMaterial && (
                <div className="modal-overlay" onClick={() => setSelectedMaterial(null)}>
                    <div className="modal-content teacher-material-view-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div>
                                <div
                                    className="teacher-material-category"
                                    style={{ background: getCategoryColor(selectedMaterial.category), marginBottom: '10px' }}
                                >
                                    {getCategoryIcon(selectedMaterial.category)}
                                    {getCategoryName(selectedMaterial.category)}
                                </div>
                                <h2>{selectedMaterial.title}</h2>
                            </div>
                            <button onClick={() => setSelectedMaterial(null)} className="modal-close">
                                <FiX />
                            </button>
                        </div>

                        {selectedMaterial.content ? (
                            <div className="teacher-material-view-content">
                                {selectedMaterial.content}
                            </div>
                        ) : (
                            <div className="teacher-material-view-empty">Содержимое не заполнено</div>
                        )}

                        {selectedMaterial.tags && (
                            <div className="teacher-material-tags teacher-material-view-tags">
                                {selectedMaterial.tags.split(',').map((tag, idx) => (
                                    <span key={idx} className="teacher-material-tag">
                                        <FiTag size={12} /> {tag.trim()}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Модальное окно создания/редактирования */}
            {showCreateModal && editingMaterial && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
                        <div className="modal-header">
                            <h2>{editingMaterial.id ? 'Редактировать материал' : 'Создать материал'}</h2>
                            <button onClick={() => setShowCreateModal(false)} className="modal-close">×</button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Название *</label>
                                <input
                                    type="text"
                                    value={editingMaterial.title}
                                    onChange={(e) => setEditingMaterial({ ...editingMaterial, title: e.target.value })}
                                    placeholder="Название материала"
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Категория</label>
                                <select
                                    value={editingMaterial.category}
                                    onChange={(e) => setEditingMaterial({ ...editingMaterial, category: e.target.value })}
                                >
                                    <option value="plan">План урока</option>
                                    <option value="answers">Ответы</option>
                                    <option value="notes">Заметки</option>
                                    <option value="tips">Подсказки</option>
                                    <option value="other">Другое</option>
                                </select>
                            </div>
                            
                            <div className="form-group">
                                <label>Содержание</label>
                                <textarea
                                    value={editingMaterial.content}
                                    onChange={(e) => setEditingMaterial({ ...editingMaterial, content: e.target.value })}
                                    rows={10}
                                    placeholder="Текст материала..."
                                    style={{ fontFamily: 'inherit' }}
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Теги (через запятую)</label>
                                <input
                                    type="text"
                                    value={editingMaterial.tags || ''}
                                    onChange={(e) => setEditingMaterial({ ...editingMaterial, tags: e.target.value })}
                                    placeholder="грамматика, Present Simple, уровень A1"
                                />
                            </div>
                        </div>
                        
                        <div className="modal-footer">
                            <button 
                                onClick={() => setShowCreateModal(false)} 
                                style={{
                                    padding: '10px 20px',
                                    background: '#f0f0f0',
                                    color: '#666',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Отмена
                            </button>
                            <button 
                                onClick={handleSave} 
                                disabled={!editingMaterial.title}
                                style={{
                                    padding: '10px 20px',
                                    background: editingMaterial.title ? '#4a90e2' : '#ccc',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: editingMaterial.title ? 'pointer' : 'not-allowed',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                {editingMaterial.id ? 'Сохранить' : 'Создать'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

export default TeacherMaterialsPage;
